import _ from "lodash";
import { getInitialValues } from "../pages/NewAvaliacao/formData";
import DatePickerInput from "../components/DatePickerInput";
import MetricInput from "../components/MetricInput";
import TextInput from "../components/TextInput";
import SelectInputFromForm from "../components/SelectInputFromForm";
import SingleMetricInput from "../components/SingleMetricInput";

export function isFieldVisible(field: any, values: any) {
  if (field.DependsOnFieldKey) {
    const dependentKeyFound = Object.keys(values).includes(
      field.DependsOnFieldKey
    );
    if (!dependentKeyFound) {
      return false;
    }
    if (
      field.DependsOnFieldValue &&
      (!values[field.DependsOnFieldKey] ||
        values[field.DependsOnFieldKey] !== field.DependsOnFieldValue)
    ) {
      return false;
    }
  }
  return true
}


export function isFieldFilled(fieldKey: any, values: any[], allFields: any[]) {
  const fieldFound = allFields.find((field) => field.chave === fieldKey);
  if (fieldFound && fieldFound.component === "MetricInput") {
    return !!values[fieldKey]?.metric && !!values[fieldKey]?.value;
  }

  return !!values[fieldKey];
}

export const calculateFormPercentage = async (values: any[], secondForm: any[]) => {
  const allFormRequiredFields = secondForm
    .map((form) => (form.campos ? form.campos : []))
    .flat()
    .filter(
      (field) =>
        !!field.obrigatorio &&
        !!field.chave &&
        field.component !== "ScoreCalculator" &&
        field.component !== "CalculoAutomatico"
    );
  const firstFormRequiredFields = (await getInitialValues())
    .filter((field) => !!field.required && !!field.type && !['horario', 'date'].includes(field.type))
    .map((field) => ({ ...field, chave: field.type }));

  if (allFormRequiredFields.length) {
    const allRequiredFields = [
      ...allFormRequiredFields.map((field) => field.chave),
      ...firstFormRequiredFields.map((field) => field.type),
    ].filter((key) => {
      const fieldFound = allFormRequiredFields.find(
        (field) => field.chave === key
      );
      if (fieldFound && !isFieldVisible(fieldFound, values)) {
        return false;
      }
      return true;
    });

    const requiredFieldsFound = allRequiredFields.filter((key) =>
      isFieldFilled(key, values, [
        ...allFormRequiredFields,
        ...firstFormRequiredFields,
      ])
    );

    const completionPercentage =
      requiredFieldsFound.length / allRequiredFields.length;
    return (completionPercentage * 100).toFixed(0);
  }
};

const isEmptyObject = (obj: any) => {
	return Object.values(obj).some((value) => {
		return (
			value === null ||
			value === undefined ||
			(typeof value === 'string' && value.trim() === '') ||
			(typeof value === 'object' && Object.keys(value).length === 0) ||
			(Array.isArray(value) && value.length === 0)
		);
	});
};

export const handleRequiredFields = (requiredFields: any[], insertedValues: any) => {
	const result: any = [];

	requiredFields.forEach((field) => {
		const hasValue =
			insertedValues?.hasOwnProperty(field) &&
			insertedValues[field] !== null &&
			insertedValues[field] !== undefined &&
			(typeof insertedValues[field] !== 'string' ||
				insertedValues[field].trim() !== '') &&
			!(
				typeof insertedValues[field] === 'object' &&
				isEmptyObject(insertedValues[field])
			) &&
			!(
				Array.isArray(insertedValues[field]) &&
				insertedValues[field].length === 0
			);

		result.push({ [field]: hasValue });
	});

	return result;
};

export const getFormErrors = (values: any, secondForm: any) => {
  for (const entry of _.values(secondForm)) {
    for (const campo of entry.campos ?? []) {
      const value = values[campo.chave || campo.Chave];
      const isRequiredField = campo.obrigatorio ?? false;
      const fieldName = campo.nome || campo.Nome;
      const componentName = campo.component;
      const isVisible = isFieldVisible(campo, values)

      if ((!value && !isRequiredField) || !isVisible) {
        continue;
      }

      if (componentName === "Multiplaescolha") {
        if (!campo.PermitirMultiplasSelecoes && !value) {
          return [`Preencha o campo:`, fieldName]
        }
        if (campo.PermitirMultiplasSelecoes && (!value || !Array.isArray(value) || value.length === 0)) {
          return [`Preencha o campo:`, fieldName]
        }
      }
    }
  }

  return null;
};

interface Props {
  item: any, values: any, handleFormItem: any
}

export function FirstFormFields(props: Props) {
  const { item, handleFormItem, values } = props

  if (item.type === "date") {
    return (
      <DatePickerInput
        id={item.type}
        handleFormItem={handleFormItem}
        value={values[item.type]}
      />
    );
  }

  if (item.type === "altitude") {
    return (
      <MetricInput
        id={item.type}
        handleFormItem={handleFormItem}
        value={values[item.type] ?? { metric: "m" }}
        options={item.options}
      />
    );
  }

  if (item.type === "area") {
    return (
      <MetricInput
        id={item.type}
        handleFormItem={handleFormItem}
        value={values[item.type] ?? { metric: "m2" }}
        options={item.options}
      />
    );
  }

  if (item.type === "perimetro") {
    return (
      <MetricInput
        id={item.type}
        handleFormItem={handleFormItem}
        value={values[item.type] ?? { metric: "m" }}
        options={item.options}
      />
    );
  }

  if (item.type === "horario") {
    return (
      <DatePickerInput
        id={item.type}
        handleFormItem={handleFormItem}
        value={values[item.type]}
        mode="time"
      />
    );
  }

  if (
    item.type === "plot" ||
    item.type === "visit.goal" ||
    item.type === "email" ||
    item.type === "property" ||
    item.type === "client" ||
    item.type === "bairro" ||
    item.type === "regional" ||
    item.type === "cidade" ||
    item.type === "endereco"
  ) {
    return (
      <TextInput
        placeholder={item.placeholder}
        onChange={(e) => handleFormItem(e.target.value, item.type)}
        variant="outlined"
        value={values[item.type as keyof typeof values] ?? ""}
      />
    );
  }

  if (item.type === "inclinacao") {
    const value = values[item.type as keyof typeof values]
    return (
      <SingleMetricInput 
        value={value}
        id={item.type}
        placeholder={item.placeholder}
        handleFormItem={handleFormItem}
      />
    )
  }

  return (
    <SelectInputFromForm
      id={item.type}
      value={values[item.type]}
      options={item.options ?? []}
      onChange={(e) => handleFormItem(e.target.value, item.type)}
    />
  );
};